import { useState } from "react";
import { BgColorsOutlined, CheckOutlined } from "@ant-design/icons";
import { Button, Flex, Form, Typography } from "../ui";
import { FormDrawer } from "../components/FormDrawer";
import { ThemeList } from "./ThemeList";
import { useAppTheme } from "./useAppTheme";
import { themeLabel, type ThemeId } from "./themeCatalog";
import { drawerWidths } from "../components/drawerWidths";

/** Gemeinsame Standardbreite des Theme-Panels. */
const THEME_DRAWER_WIDTH = drawerWidths.standard;

/**
 * Theme-Auswahl des Benutzers im gemeinsamen `FormDrawer` (Aktionen oben rechts wie jeder Drawer), ohne Maske:
 * Jede Kachel zeigt eine verkleinerte Seite im Theme, die Auswahl wirkt zusätzlich sofort auf die laufende
 * App. „übernehmen“ speichert, Schließen ohne Übernahme (auch Escape) stellt das vorige Theme wieder her.
 * Pfeiltasten wechseln, Enter übernimmt.
 */
export function ThemeDrawer({ open, onClose, tr }: { open: boolean; onClose: () => void; tr: (key: string) => string }) {
  const { availableThemes, availability, themeId, previewThemeId, setPreviewThemeId, setThemeId } = useAppTheme();
  const [initialThemeId, setInitialThemeId] = useState<ThemeId | null>(null);
  // Beim Öffnen gilt das bis dahin wirksame Theme als Ausgangswert.
  if (open && initialThemeId === null) setInitialThemeId(themeId);
  if (!open && initialThemeId !== null) setInitialThemeId(null);

  const close = () => {
    setPreviewThemeId(null);
    onClose();
  };
  const changed = initialThemeId !== null && themeId !== initialThemeId;
  const apply = () => {
    if (changed) setThemeId(previewThemeId ?? themeId);
    onClose();
  };

  return (
    <FormDrawer
      open={open}
      onCancel={close}
      dirty={changed}
      title={tr("Theme.drawerTitle")}
      width={THEME_DRAWER_WIDTH}
      onSave={apply}
      submitDisabled={!changed}
      submitText={tr("Theme.apply")}
      submitIcon={<CheckOutlined />}
      cancelText={tr("Theme.cancel")}
      autoFocusFirstField={false}
      drawerProps={{ mask: false }}
    >
      <ThemeList
        label={tr("Theme.drawerTitle")}
        tr={tr}
        selectedKey={themeId}
        onSelect={(_, definition) => setPreviewThemeId(definition.id === initialThemeId ? null : definition.id)}
        onConfirm={apply}
        items={availableThemes.map((definition) => ({
          definition,
          badges: [
            ...(availability?.tenantDefaultThemeId === definition.id ? [tr("Theme.badge.teamDefault")] : []),
            ...(availability?.tenantDefaultThemeId == null && availability?.appDefaultThemeId === definition.id ? [tr("Theme.badge.appDefault")] : []),
          ],
        }))}
      />
    </FormDrawer>
  );
}

/** Formularzeile für Benutzereinstellungen: aktuelles Design und Einstieg in die Auswahl. */
export function ThemeSettingField({ tr }: { tr: (key: string) => string }) {
  const { themeId } = useAppTheme();
  const [open, setOpen] = useState(false);
  return (
    <Form.Item label={tr("Theme.menu")}>
      <Flex align="center" gap="small" wrap="wrap">
        <Typography.Text>{themeLabel(themeId, tr)}</Typography.Text>
        <Button icon={<BgColorsOutlined />} onClick={() => setOpen(true)}>{tr("Theme.choose")}</Button>
      </Flex>
      <ThemeDrawer open={open} onClose={() => setOpen(false)} tr={tr} />
    </Form.Item>
  );
}
